import React, { useReducer, createContext } from "react";
import storage from "../utils/storage";
import {
  SOLANA_DEVNET,
  SOLANA_MAINNET,
  SOLANA_TESTNET,
} from "../constants/urls";

export const AppContext = createContext();

export const AVAILABLE_NETWORKS = ["MAINNET", "TESTNET", "DEVNET"];
export const NETWORKS = {
  MAINNET: "MAINNET",
  TESTNET: "TESTNET",
  DEVNET: "DEVNET",
};

const NETWORK_URLS = {
  [NETWORKS.MAINNET]: SOLANA_MAINNET,
  [NETWORKS.TESTNET]: SOLANA_TESTNET,
  [NETWORKS.DEVNET]: SOLANA_DEVNET,
};

const resolveNetwork = (value) => {
  const key = String(value || "").toUpperCase();
  return AVAILABLE_NETWORKS.includes(key) ? key : null;
};

// Every visit starts on the build's network (APP_NETWORK, mainnet in
// production). A switch lasts for this tab, through reloads and in-app
// navigation, but not into the next visit: it lives in sessionStorage, so
// nobody comes back days later still on devnet without noticing.
const NETWORK_KEY = "NETWORK_TYPE";
const tabNetwork = {
  get: () => {
    try {
      return sessionStorage.getItem(NETWORK_KEY);
    } catch (e) {
      return null;
    }
  },
  set: (network) => {
    try {
      sessionStorage.setItem(NETWORK_KEY, network);
    } catch (e) {
      // storage blocked: the choice just lasts until the page reloads
    }
  },
};
// Older builds kept the choice in localStorage, where it outlived the visit.
storage.remove(NETWORK_KEY);

const DEFAULT_NETWORK =
  resolveNetwork(tabNetwork.get()) ||
  resolveNetwork(process.env.APP_NETWORK) ||
  NETWORKS.MAINNET;

// Per-network user-supplied RPC endpoints. The public solana.com endpoints
// block getProgramAccounts / getParsedTokenAccountsByOwner from browsers
// (403 Access forbidden), so most tools need a real RPC.
const loadCustomRpc = () => {
  const raw = storage.get("CUSTOM_RPC");
  return raw && typeof raw === "object" ? raw : {};
};

export const endpointFor = (network, customRpc) => {
  const custom = customRpc?.[network];
  if (typeof custom === "string" && /^https?:\/\//.test(custom)) return custom;
  return NETWORK_URLS[network];
};

const CUSTOM_RPC = loadCustomRpc();

const INITIAL_STATE = {
  currentNetwork: DEFAULT_NETWORK,
  currentNetworkURL: endpointFor(DEFAULT_NETWORK, CUSTOM_RPC),
  customRpc: CUSTOM_RPC,
};

// It converts actions to the states.
const appReducer = (state, action) => {
  const { type, payload } = action;
  switch (type) {
    case "SET_NETWORK": {
      const network = resolveNetwork(payload);
      if (!network) return state;
      tabNetwork.set(network);
      return {
        ...state,
        currentNetwork: network,
        currentNetworkURL: endpointFor(network, state.customRpc),
      };
    }
    case "SET_RPC": {
      const { network, url } = payload || {};
      const target = resolveNetwork(network) || state.currentNetwork;
      const next = { ...state.customRpc };
      if (url && /^https?:\/\//.test(url.trim())) next[target] = url.trim();
      else delete next[target];
      storage.set("CUSTOM_RPC", next);
      return {
        ...state,
        customRpc: next,
        currentNetworkURL: endpointFor(state.currentNetwork, next),
      };
    }
    default:
      return state;
  }
};

export const AppContextProvider = ({ children }) => {
  const store = useReducer(appReducer, {
    ...INITIAL_STATE,
  });
  return <AppContext.Provider value={store}>{children}</AppContext.Provider>;
};
